import {
  FullscreenControl,
  GeolocateControl,
  Map,
  Marker,
  NavigationControl,
  Popup,
} from "bkoi-gl";
import { debounce } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import UserIcon from "../../../assets/icons/UserIcon";
import EmployeeInfoItem from "../../../components/common/EmployeeInfoItem";
import InfoTitle from "../../../components/common/InfoTitle";
import Loader from "../../../components/ui/Loader";
import TimelineItem from "../../../components/Visit/TimelineItem";
import colors from "../../../constants/colors";
import {
  BARIKOI_API_KEY,
  dateFormat,
  dbDateFormat,
  dbTimeFormat,
  timeFormat,
} from "../../../constants/constants";
import { useGetEmployeeDetailsQuery } from "../../../features/employee/employeeApi";
import {
  useBariKoiApiHitMutation,
  useGetEmployeeTrackingHistoryQuery,
} from "../../../features/employeeTracking/employeeTrackingApi";
import useError from "../../../hooks/useError";
import { LocationType } from "../../../types/tracking";
import { parseJSON } from "../../../utils/string";

type EmployeeMapAndDetailsProps = {
  date?: string;
};

export default function EmployeeMapAndDetails({
  date,
}: EmployeeMapAndDetailsProps) {
  const [mapError, setMapError] = useState(false);
  const { viewId } = useParams();
  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);
  const {
    isLoading: isEmployeeDetailsLoading,
    isError: isEmployeeDetailsError,
    error: employeeDetailsError,
    data: employeeDetailsData,
  } = useGetEmployeeDetailsQuery(viewId || "", {
    skip: !viewId,
  });
  const { isLoading, isError, error, data, isFetching } =
    useGetEmployeeTrackingHistoryQuery(
      {
        params: {
          id: viewId || "",
          date: date || "",
        },
      },
      {
        skip: !viewId || !date,
      }
    );
  const [bariKoiApiHit] = useBariKoiApiHitMutation();
  useError(
    isEmployeeDetailsError,
    employeeDetailsError,
    "Get employee data fail"
  );
  useError(isError, error, "Get employee history data fail");

  const employeeInfo = employeeDetailsData?.data?.employee;

  const timelineData = useMemo(
    () => data?.data?.map((el) => {
      const parseJson =
        typeof el?.address === "string"
          ? (parseJSON(el?.address) as LocationType)
          : el?.address;

      return {
        ...el,
        address: {
          address: parseJson?.address || "",
          lng: parseJson?.lng || 0,
          lat: parseJson?.lat || 0,
        },
        time: `${moment(el?.time, dbTimeFormat).format(timeFormat)} - ${moment(
          el?.date,
          dbDateFormat
        ).format(dateFormat)}`,
      };
    }),
    [data]
  );

  const polygonCoordinates: {
    geo: [number, number];
    address: string;
  }[] = useMemo(
    () =>
      timelineData
        ?.map((point) => ({
          geo: [point.address.lng, point.address.lat] as [number, number],
          address: point.address.address,
        }))
        .filter(({ geo: [lng, lat] }) =>
          Number.isFinite(lng) && Number.isFinite(lat) &&
          Math.abs(lng) <= 180 && Math.abs(lat) <= 90 &&
          (lng !== 0 || lat !== 0)
        ) || [],
    [timelineData]
  );

  const mapContainer = useRef(null) as any;
  const map = useRef(null) as any;
  const markersRef = useRef<{ [key: string]: any }>({});

  const handleHover = useMemo(
    () =>
      debounce((lng: number, lat: number) => {
        if (map.current) {
          map.current.flyTo({
            center: [lng, lat],
            speed: 2,
          });
        }
      }, 10),
    []
  );

  const handleTimelineHover = (lng: number, lat: number, enlarge: boolean) => {
    const markerId = `${lng}-${lat}`;
    setHoveredMarker(enlarge ? markerId : null);
  };

  useEffect(() => {
    if (isLoading || isFetching || !mapContainer.current) return;

    setMapError(false);
    const instance = new Map({
      container: mapContainer.current,
      center: polygonCoordinates[0]?.geo || [90.39017821904588, 23.719800220780733],
      zoom: polygonCoordinates.length ? 15 : 12,
      doubleClickZoom: false,
      accessToken: BARIKOI_API_KEY,
      attributionControl: { compact: false },
    });
    map.current = instance;
    let styleReady = false;
    const onMapError = (event: { sourceId?: string; tile?: unknown }) => {
      // Tile/font/source failures can occur on a usable map. The banner is
      // reserved for a failure to load the base style itself.
      if (!styleReady && !event?.sourceId && !event?.tile) setMapError(true);
    };
    const onStyleLoad = () => {
      styleReady = true;
      setMapError(false);
    };
    const onMapReady = () => setMapError(false);
    instance.on("error", onMapError);
    instance.on("style.load", onStyleLoad);
    instance.on("load", onMapReady);
    instance.on("idle", onMapReady);
    if (BARIKOI_API_KEY) bariKoiApiHit();

    instance.addControl(new FullscreenControl());
    instance.addControl(new NavigationControl());
    instance.addControl(new GeolocateControl());

    instance.on("style.load", () => {
      if (polygonCoordinates.length > 1) {
        instance.addSource("route", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: polygonCoordinates.map((item) => item.geo),
            },
          },
        });
        instance.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": colors.tintNeutral, "line-width": 2 },
        });
      }
    });

    const popups: any[] = [];
    const markerPositions: { geo: [number, number]; marker: any }[] = [];
    polygonCoordinates.forEach((item) => {
      const markerId = `${item.geo[0]}-${item.geo[1]}`;
      // GPS samples while stationary can differ by a few metres. Reuse the
      // pin within 10 m, keeping every sample in the timeline and route.
      const existing = markerPositions.find(({ geo }) => {
        const latitudeMeters = (item.geo[1] - geo[1]) * 111320;
        const longitudeDelta = Math.abs(item.geo[0] - geo[0]);
        const longitudeMeters = Math.min(longitudeDelta, 360 - longitudeDelta) *
          111320 * Math.cos((item.geo[1] + geo[1]) * Math.PI / 360);
        return Math.hypot(latitudeMeters, longitudeMeters) <= 10;
      });
      if (existing) {
        markersRef.current[markerId] = existing.marker;
        return;
      }
      const marker = new Marker({ color: colors.primary, draggable: false, scale: 1 })
        .setLngLat(item.geo)
        .addTo(instance);
      const content = document.createElement("p");
      content.className = "text-center text-header";
      content.textContent = item.address;
      const popup = new Popup({ focusAfterOpen: false, offset: [0, -35] })
        .setLngLat(item.geo)
        .setDOMContent(content);
      popups.push(popup);
      markersRef.current[markerId] = marker;
      markerPositions.push({ geo: item.geo, marker });
      marker.getElement().addEventListener("mouseenter", () => {
        setHoveredMarker(markerId);
        popup.addTo(instance);
      });
      marker.getElement().addEventListener("mouseleave", () => {
        setHoveredMarker(null);
        popup.remove();
      });
    });

    return () => {
      instance.off("error", onMapError);
      instance.off("style.load", onStyleLoad);
      instance.off("load", onMapReady);
      instance.off("idle", onMapReady);
      handleHover.cancel();
      popups.forEach((popup) => popup.remove());
      markersRef.current = {};
      instance.remove();
      map.current = null;
    };
  }, [date, isFetching, isLoading, polygonCoordinates, bariKoiApiHit, handleHover]);

  useEffect(() => {
    const activeMarker = hoveredMarker ? markersRef.current[hoveredMarker] : null;
    // Scale the icon, leaving the map library's positioning transform intact.
    new Set(Object.values(markersRef.current)).forEach((marker) => {
      const icon = marker.getElement().querySelector("svg");
      if (icon) {
        icon.style.transformOrigin = "bottom center";
        icon.style.transform = marker === activeMarker ? "scale(1.3)" : "scale(1)";
      }
    });
  }, [hoveredMarker]);

  const containerStyles = {
    width: "100%",
    height: "100vh",
    minHeight: "400px",
    overflow: "hidden",
  };

  return (
    <div className="grid md:grid-cols-8 gap-6 mb-6">
      <div className="md:col-span-3">
        <div className="bg-white rounded-md border border-extra-light">
          <div className="p-6">
            <InfoTitle className="mb-6" icon={<UserIcon />}>
              Employee Info
            </InfoTitle>
            {isEmployeeDetailsLoading ? (
              <Loader />
            ) : (
              <EmployeeInfoItem
                img={employeeInfo?.member?.profile?.pro_pic || undefined}
                name={employeeInfo?.member?.profile?.name}
                id={employeeInfo?.employee_id}
                designation={employeeInfo?.role?.name}
                department={employeeInfo?.role?.department?.name}
                email={employeeInfo?.member?.profile?.email}
              />
            )}
          </div>

          <div className="mt-4">
            <h4 className="text-header text-xl font-semibold pb-6 px-6">
              Timeline
            </h4>
            <ul className="max-h-[500px] overflow-y-auto">
              {isLoading || isFetching ? (
                <li className="h-40">
                  <Loader />
                </li>
              ) : (
                timelineData?.map((item, index) => (
                  <TimelineItem
                    length={timelineData?.length}
                    index={index}
                    key={index}
                    time={item?.time}
                    details={item?.address?.address}
                    color={index === 0 ? "#1b4dff" : "#FF3838"}
                    onMouseEnter={() => {
                      handleHover(
                        item?.address?.lng as number,
                        item?.address?.lat as number
                      );
                      handleTimelineHover(
                        item?.address?.lng as number,
                        item?.address?.lat as number,
                        true
                      );
                    }}
                    onMouseLeave={() => {
                      handleTimelineHover(
                        item?.address?.lng as number,
                        item?.address?.lat as number,
                        false
                      );
                    }}
                  />
                ))
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="md:col-span-5 min-w-0">
        {mapError && (
          <div role="alert" className="mb-2 rounded-md border border-extra-light bg-white p-4 text-header">
            The map could not be loaded. Check your connection and reload the page.
          </div>
        )}
        {isFetching || isLoading ? (
          <div className="mb-2">
            <Loader />
          </div>
        ) : null}
        <div className="rounded-md">
          <div ref={mapContainer} style={containerStyles} />
        </div>
      </div>
    </div>
  );
}
